import React from 'react';
import { FaSort, FaSortUp, FaSortDown, FaSearch, FaKey } from 'react-icons/fa';

interface UserManagementProps {
    users: any[];
    pagination: any;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    setPagination: (pagination: any) => void;
    loadData: () => void;
    handleViewUser: (user: any) => void;
    handleResetPassword: (user: any) => void;

    sortField: string;
    setSortField: (field: string) => void;
    sortOrder: 'ASC' | 'DESC';
    setSortOrder: (order: 'ASC' | 'DESC') => void;
    handleUpdateRole: (userId: string, newRole: 'admin' | 'user') => Promise<void>;
}

const UserManagement: React.FC<UserManagementProps> = ({
    users,
    pagination,
    searchTerm,
    setSearchTerm,
    setPagination,
    loadData,
    handleViewUser,
    handleResetPassword,

    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    handleUpdateRole
}) => {
    const [confirmConfig, setConfirmConfig] = React.useState<{
        isOpen: boolean;
        title: string;
        message: string;
        action: () => void;
        isDestructive?: boolean;
    }>({
        isOpen: false,
        title: '',
        message: '',
        action: () => { },
        isDestructive: false
    });

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortField(field);
            setSortOrder('DESC');
        }
    };

    const requestRoleChange = (user: any, newRole: 'admin' | 'user') => {
        const isPromoting = newRole === 'admin';
        setConfirmConfig({
            isOpen: true,
            title: isPromoting ? 'Xác nhận cấp quyền Admin' : 'Xác nhận hủy quyền Admin',
            message: isPromoting
                ? `Bạn có chắc chắn muốn cấp quyền QUẢN TRỊ VIÊN cho tài khoản ${user.email}? Họ sẽ có toàn quyền truy cập hệ thống.`
                : `Bạn có chắc chắn muốn chuyển tài khoản ${user.email} xuống thành người dùng thường?`,
            action: () => handleUpdateRole(user.id, newRole),
            isDestructive: !isPromoting // Demoting is considered destructive/warning
        });
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortField !== field) return <FaSort className="w-3 h-3 text-gray-400 inline-block ml-1" />;
        return sortOrder === 'ASC'
            ? <FaSortUp className="w-3 h-3 text-blue-500 inline-block ml-1" />
            : <FaSortDown className="w-3 h-3 text-blue-500 inline-block ml-1" />;
    };

    const RoleBadge = ({ user, compact = false }: { user: any; compact?: boolean }) => {
        const roleConfig: Record<string, { label: string; shortLabel: string; color: string }> = {
            admin: { label: 'Quản trị viên', shortLabel: 'QTV', color: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800' },
            lop_truong: { label: 'Lớp trưởng', shortLabel: 'LT', color: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' },
            bi_thu: { label: 'Bí thư', shortLabel: 'BT', color: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800' },
            pho_bi_thu: { label: 'Phó bí thư', shortLabel: 'PBT', color: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800' },
            lop_pho_hoc_tap: { label: 'LP học tập', shortLabel: 'LPHT', color: 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800' },
            lop_pho_doi_song: { label: 'LP đời sống', shortLabel: 'LPĐS', color: 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800' },
            thu_quy: { label: 'Thủ quỹ', shortLabel: 'TQ', color: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800' },
            uy_vien: { label: 'Ủy viên', shortLabel: 'UV', color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' },
            thanh_vien: { label: 'Thành viên', shortLabel: 'TV', color: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800' },
        };

        const config = roleConfig[user.role] || roleConfig.thanh_vien;
        const displayLabel = compact ? config.shortLabel : config.label;

        return (
            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold border shadow-sm whitespace-nowrap ${config.color}`} title={config.label}>
                {displayLabel}
            </span>
        );
    };

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Control Bar - Solid Card */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                <div className="relative w-full md:w-96 group">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <FaSearch className="w-4 h-4 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                    </div>
                    <input
                        type="text"
                        placeholder="Tìm kiếm user..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && loadData()}
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none dark:text-white dark:placeholder-gray-500"
                    />
                </div>
            </div>

            {/* Data Display - Responsive: Cards on Mobile, Table on Desktop */}
            <div className="bg-transparent md:bg-white md:dark:bg-gray-800 md:rounded-2xl md:shadow-xl md:border md:border-gray-100 md:dark:border-gray-700 overflow-hidden">

                {/* Mobile Card View (< 768px) */}
                <div className="block md:hidden space-y-4">
                    {users.map((user) => (
                        <div key={user.id} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-3">
                            <div className="flex justify-between items-start gap-2">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 flex items-center justify-center text-sm font-bold text-blue-700 dark:text-blue-300 shadow-sm shrink-0">
                                        {user.email.substring(0, 2).toUpperCase()}
                                    </div>
                                    <div className="overflow-hidden min-w-0 flex-1">
                                        <div className="font-medium text-gray-900 dark:text-white truncate text-sm">
                                            {user.email}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                            {user.name || "Chưa đặt tên"}
                                        </div>
                                    </div>
                                </div>
                                <div className="shrink-0">
                                    <RoleBadge user={user} compact />
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-700">
                                <span className="truncate">Tham gia: {new Date(user.created_at).toLocaleDateString('vi-VN')}</span>
                                <div className="flex gap-2 shrink-0 ml-2">
                                    <button
                                        onClick={() => handleResetPassword(user)}
                                        className="text-amber-600 dark:text-amber-400 font-medium hover:text-amber-700 p-1"
                                        title="Reset Password"
                                    >
                                        <FaKey />
                                    </button>
                                    <button
                                        onClick={() => handleViewUser(user)}
                                        className="text-blue-600 dark:text-blue-400 font-medium hover:underline text-xs whitespace-nowrap"
                                    >
                                        Chi tiết &rarr;
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}

                    {users.length === 0 && (
                        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl text-center text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700">
                            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-50 dark:bg-gray-700 flex items-center justify-center text-xl"><FaSearch /></div>
                            <p>Không tìm thấy user nào</p>
                        </div>
                    )}
                </div>

                {/* Desktop Table View (>= 768px) */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead>
                            <tr className="bg-gray-50/80 dark:bg-gray-900/50">
                                {[
                                    { key: 'email', label: 'Email' },
                                    { key: 'name', label: 'Tên' },
                                    { key: 'role', label: 'Vai Trò' },
                                    { key: 'created_at', label: 'Ngày Tạo' }
                                ].map((col) => (
                                    <th
                                        key={col.key}
                                        onClick={() => handleSort(col.key)}
                                        className={`px-6 py-5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors select-none ${col.key === 'name' || col.key === 'created_at' ? 'hidden sm:table-cell' : ''} ${col.key === 'created_at' ? 'hidden md:table-cell' : ''}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            {col.label} <SortIcon field={col.key} />
                                        </div>
                                    </th>
                                ))}
                                <th className="px-6 py-5 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Thao Tác
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {users.map((user) => (
                                <tr
                                    key={user.id}
                                    className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors group"
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 flex items-center justify-center text-sm font-bold text-blue-700 dark:text-blue-300 shadow-sm">
                                                {user.email.substring(0, 2).toUpperCase()}
                                            </div>
                                            <div className="font-medium text-gray-900 dark:text-white truncate max-w-[150px] sm:max-w-xs">
                                                {user.email}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="hidden sm:table-cell px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                        {user.name || <span className="text-gray-400 italic">Chưa đặt tên</span>}
                                    </td>
                                    <td className="px-6 py-4">
                                        <RoleBadge user={user} />
                                    </td>
                                    <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-500 dark:text-gray-400 font-mono">
                                        {new Date(user.created_at).toLocaleDateString('vi-VN')}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => handleResetPassword(user)}
                                                title="Reset Mật Khẩu"
                                                className="px-3 py-2 text-sm font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-all hover:shadow-md active:scale-95"
                                            >
                                                <FaKey />
                                            </button>
                                            <button
                                                onClick={() => handleViewUser(user)}
                                                className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all hover:shadow-md active:scale-95"
                                            >
                                                Chi Tiết
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-16 text-center text-gray-500 dark:text-gray-400">
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="w-16 h-16 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-gray-400 text-3xl shadow-inner"><FaSearch /></div>
                                            <p className="text-lg font-medium">Không tìm thấy người dùng nào</p>
                                            <p className="text-sm">Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination - Solid Card */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    Hiển thị <span className="text-gray-900 dark:text-white">{users.length}</span> / <span className="text-gray-900 dark:text-white">{pagination.total}</span> kết quả
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setPagination({ ...pagination, page: Math.max(1, pagination.page - 1) })}
                        disabled={pagination.page === 1}
                        className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all text-sm font-semibold shadow-sm"
                    >
                        Trước
                    </button>
                    <div className="px-4 py-2 bg-blue-600 text-white font-bold rounded-lg text-sm min-w-[3rem] text-center shadow-md shadow-blue-500/30">
                        {pagination.page}
                    </div>
                    <button
                        onClick={() => setPagination({ ...pagination, page: Math.min(pagination.totalPages, pagination.page + 1) })}
                        disabled={pagination.page >= pagination.totalPages}
                        className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all text-sm font-semibold shadow-sm"
                    >
                        Sau
                    </button>
                </div>
            </div>

            {/* Confirm Dialog */}
            {confirmConfig.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-md m-4 border border-gray-100 dark:border-gray-700 transform transition-all scale-100 animate-scaleIn">
                        <h3 className={`text-xl font-bold mb-3 ${confirmConfig.isDestructive ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                            {confirmConfig.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                            {confirmConfig.message}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                            >
                                Hủy bỏ
                            </button>
                            <button
                                onClick={() => {
                                    confirmConfig.action();
                                    setConfirmConfig({ ...confirmConfig, isOpen: false });
                                }}
                                className={`px-4 py-2 text-white rounded-lg font-medium shadow-sm transition-all active:scale-95 ${confirmConfig.isDestructive
                                    ? 'bg-red-600 hover:bg-red-700 shadow-red-500/30'
                                    : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/30'
                                    }`}
                            >
                                Xác nhận
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
